/**
 * Review-queue text search (TRA-956) — one box that matches everything the
 * client already holds about a row: the flag's summary/notes, tags, flag
 * source, trace/conversation ids, the model map, and the client-metadata
 * map (organization = client_tenant_name and client_tenant_id, environment,
 * traffic type, session, initiator).
 *
 * Tokens are ANDed: "cartage staging" narrows to rows matching both. The
 * two side-channel maps hydrate after the queue paints, so a row's model
 * and metadata become searchable when their map arrives — searching before
 * that matches on the label fields alone rather than waiting.
 */

import type { ReviewQueueItem } from '@/api/generated/model'

import type { QueueClientMetadataIndex } from './clientMetadata'
import type { QueueModelIndex } from './flagSource'

function haystack(
  item: ReviewQueueItem,
  modelByKey: QueueModelIndex,
  metadataByKey: QueueClientMetadataIndex,
): string {
  const metadata = metadataByKey[item.trace_key]
  const parts: (string | null | undefined)[] = [
    item.summary,
    item.notes,
    item.created_by,
    item.trace_id,
    item.conversation_id,
    ...item.tags,
    modelByKey[item.trace_key],
    metadata?.client_tenant_id,
    metadata?.client_tenant_name,
    metadata?.environment,
    metadata?.traffic_type,
    metadata?.session_id,
    metadata?.initiated_by,
  ]
  return parts.filter((part) => part != null && part !== '').join('\n').toLowerCase()
}

/** Whether a row survives the search box. An empty query matches every row. */
export function matchesQueueSearch(
  item: ReviewQueueItem,
  query: string,
  modelByKey: QueueModelIndex,
  metadataByKey: QueueClientMetadataIndex,
): boolean {
  const tokens = query.toLowerCase().split(/\s+/).filter((token) => token !== '')
  if (tokens.length === 0) return true
  const target = haystack(item, modelByKey, metadataByKey)
  return tokens.every((token) => target.includes(token))
}
